import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Panel, LoadingState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { HeartPulse, Server, Database, Wifi, Cpu, Plug, Search, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

function StatusPill({ status }) {
  const map = {
    online: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    connected: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    reconciled: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    idle: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    disabled: "text-slate-400 bg-slate-500/10 border-slate-600/40",
    discrepancies_found: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    disconnected: "text-slate-400 bg-slate-500/10 border-slate-600/40",
    not_connected: "text-slate-400 bg-slate-500/10 border-slate-600/40",
    error: "text-rose-400 bg-rose-500/10 border-rose-500/30",
  };
  return <span className={cn("text-[10px] font-mono px-2 py-0.5 rounded border uppercase", map[status] || map.disconnected)}>{String(status).replace("_", " ")}</span>;
}

export default function SystemHealth() {
  const { data: health, isLoading } = useQuery({
    queryKey: ["system-health"],
    queryFn: async () => (await api.get("/system/health")).data,
    refetchInterval: 15000,
  });

  const { data: audit } = useQuery({
    queryKey: ["audit"],
    queryFn: async () => (await api.get("/audit", { params: { limit: 500 } })).data,
  });

  const [auditSearch, setAuditSearch] = useState("");
  const [auditCat, setAuditCat] = useState("all");
  const [auditOrder, setAuditOrder] = useState("desc");
  const [auditPage, setAuditPage] = useState(1);
  const AUDIT_PAGE_SIZE = 12;

  if (isLoading) return <LoadingState />;

  const services = [
    ["Application", health.app_status, Server],
    ["Database", health.db_status, Database],
    ["Market Data Feed", health.market_data_status, Wifi],
    ["Background Worker", health.worker_status, Cpu],
    ["Strategy Engine", health.strategy_engine_status, Cpu],
    ["Trading Execution", health.trading_execution_status, Server],
    ["Accounting Reconciliation", health.accounting_reconciliation_status, Database],
  ];

  const levelColor = { info: "text-blue-400", warning: "text-amber-400", error: "text-rose-400" };

  const allAudit = audit?.items || [];
  const auditCategories = ["all", ...Array.from(new Set(allAudit.map((a) => a.category).filter(Boolean)))];
  const filteredAudit = allAudit
    .filter((a) => auditCat === "all" || a.category === auditCat)
    .filter((a) => {
      const q = auditSearch.trim().toLowerCase();
      if (!q) return true;
      return (a.action || "").toLowerCase().includes(q) || (a.detail || "").toLowerCase().includes(q) || (a.actor || "").toLowerCase().includes(q);
    })
    .sort((a, b) => (auditOrder === "desc"
      ? String(b.created_at).localeCompare(String(a.created_at))
      : String(a.created_at).localeCompare(String(b.created_at))));
  const auditTotalPages = Math.max(1, Math.ceil(filteredAudit.length / AUDIT_PAGE_SIZE));
  const pageSafe = Math.min(auditPage, auditTotalPages);
  const auditPageItems = filteredAudit.slice((pageSafe - 1) * AUDIT_PAGE_SIZE, pageSafe * AUDIT_PAGE_SIZE);

  return (
    <div>
      <PageHeader title="System Health" subtitle="Live service status · no fake connected states" icon={HeartPulse} testId="system-health-header" demo={false} />

      <div className="flex items-center justify-between gap-2 mb-4 rounded-lg border border-amber-500/25 bg-amber-500/10 px-4 py-2.5" data-testid="health-banner">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 pulse-dot" />
          <span className="text-xs font-mono font-semibold text-amber-400 uppercase tracking-wide">{health.banner}</span>
        </div>
        <span className="text-[10px] text-slate-500 font-mono">Last check: {new Date(health.last_check).toLocaleTimeString("en-IN")}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {services.map(([label, status, Icon]) => (
          <div key={label} data-testid={`service-${label.toLowerCase().replace(/ /g, "-")}`} className="rounded-lg border border-[var(--border-subtle)] bg-[var(--card)] p-4">
            <div className="flex items-center justify-between mb-2">
              <Icon className="w-4 h-4 text-slate-500" />
              <StatusPill status={status} />
            </div>
            <div className="text-sm font-medium text-slate-200">{label}</div>
          </div>
        ))}
      </div>

      {(health.accounting_advisories || []).length > 0 && (
        <Panel title="Reconciliation Advisories" testId="recon-advisories-panel" className="mb-4">
          <div className="px-4 py-3 space-y-2">
            <p className="text-[11px] text-slate-500">Books reconcile on accounting identities, but these evidence exceptions are preserved and are NOT concealed by the balanced status.</p>
            {health.accounting_advisories.map((adv, i) => (
              <div key={i} data-testid="recon-advisory" className="flex items-start gap-2 rounded-md border border-amber-500/25 bg-amber-500/10 px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-[11px] text-slate-300">
                  <div className="font-semibold text-amber-400 uppercase tracking-wide mb-0.5">{String(adv.type).replace(/_/g, " ")}</div>
                  <div>Reallocated ₹{adv.reallocated} · evidenced ₹{adv.realized_pnl_evidenced} · <span className="text-amber-400">unsupported ₹{adv.unsupported_amount}</span></div>
                  <div className="text-slate-500 mt-0.5">{adv.explanation}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Broker Connectivity" testId="broker-connectivity-panel" actions={<DemoBadge />}>
          <div className="divide-y divide-[var(--border-subtle)] max-h-[300px] overflow-y-auto">
            {health.brokers.map((b) => (
              <div key={b.name} className="flex items-center justify-between px-4 py-2.5">
                <div className="flex items-center gap-2"><Plug className="w-3.5 h-3.5 text-slate-500" /><span className="text-xs text-slate-300">{b.name}</span></div>
                <StatusPill status={b.status} />
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="System Events" testId="system-events-panel">
          <div className="divide-y divide-[var(--border-subtle)] max-h-[300px] overflow-y-auto">
            {health.events.map((e) => (
              <div key={e.id} className="flex items-start gap-3 px-4 py-2.5">
                <span className={cn("text-[10px] font-mono uppercase w-14 shrink-0", levelColor[e.level])}>{e.level}</span>
                <div className="min-w-0">
                  <div className="text-xs text-slate-300">{e.message}</div>
                  <div className="text-[10px] text-slate-600 font-mono">{new Date(e.created_at).toLocaleString("en-IN")}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="Audit History" testId="audit-panel" className="mt-4" actions={
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="audit-search" value={auditSearch} onChange={(e) => { setAuditSearch(e.target.value); setAuditPage(1); }} placeholder="Search action, detail, actor..." className="h-8 pl-8 w-[220px] text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-slate-200" />
          </div>
          <Select value={auditCat} onValueChange={(v) => { setAuditCat(v); setAuditPage(1); }}>
            <SelectTrigger data-testid="audit-category" className="h-8 w-[140px] text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200">
              {auditCategories.map((c) => <SelectItem key={c} value={c} className="capitalize">{c === "all" ? "All categories" : c}</SelectItem>)}
            </SelectContent>
          </Select>
          <button data-testid="audit-sort" onClick={() => setAuditOrder((o) => (o === "desc" ? "asc" : "desc"))} className="h-8 px-2 rounded border border-[var(--border-subtle)] text-[11px] text-slate-400 hover:bg-[var(--card-hover)]">{auditOrder === "desc" ? "Newest" : "Oldest"}</button>
        </div>
      }>
        <div className="divide-y divide-[var(--border-subtle)]" data-testid="audit-list">
          {auditPageItems.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-slate-600">No audit events match your filters.</div>
          ) : auditPageItems.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-4 py-2.5 text-xs">
              <span className="font-mono text-[10px] text-blue-400 border border-blue-500/20 rounded px-1.5 py-0.5 w-40 shrink-0 truncate">{a.action}</span>
              {a.category && <span className="text-[9px] font-mono uppercase text-slate-500 border border-slate-600/40 rounded px-1 py-0.5 shrink-0">{a.category}</span>}
              <span className="text-slate-400 flex-1 truncate">{a.detail}</span>
              <span className="text-[10px] text-slate-600 font-mono shrink-0">{new Date(a.created_at).toLocaleString("en-IN")}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-[var(--border-subtle)] text-xs text-slate-500">
          <span data-testid="audit-count">{filteredAudit.length} events</span>
          <div className="flex items-center gap-1">
            <button data-testid="audit-prev" disabled={pageSafe <= 1} onClick={() => setAuditPage((p) => Math.max(1, p - 1))} className="w-7 h-7 flex items-center justify-center rounded border border-[var(--border-subtle)] text-slate-400 hover:bg-[var(--card-hover)] disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-2 font-mono-num text-slate-300">{pageSafe} / {auditTotalPages}</span>
            <button data-testid="audit-next" disabled={pageSafe >= auditTotalPages} onClick={() => setAuditPage((p) => Math.min(auditTotalPages, p + 1))} className="w-7 h-7 flex items-center justify-center rounded border border-[var(--border-subtle)] text-slate-400 hover:bg-[var(--card-hover)] disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
